import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../../context/FamilyContext';

const steps = ['Account', 'Add Child', 'Confirm'];

/**
 * Onboarding wizard for new parent users.
 * Guides them through account setup and adding their first learner.
 */
export const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const { createLearner, error: familyError } = useFamily();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [learnerData, setLearnerData] = useState({
    firstName: '',
    lastName: '',
    gradeLevel: '3',
    dateOfBirth: ''
  });

  const handleLearnerChange = (field: string, value: string) => {
    setLearnerData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = async () => {
    try {
      setError(null);
      setLoading(true);

      if (activeStep === 1) {
        // Validate learner data
        if (!learnerData.firstName || !learnerData.lastName) {
          setError('Please enter child\'s name');
          setLoading(false);
          return;
        }

        // Create the learner. ownerId/ownerType are NOT sent here - the
        // server derives them from the authenticated caller's family
        // (api/family.js POST /learners), so passing them from the client
        // would be ignored and is misleading to include.
        await createLearner({
          firstName: learnerData.firstName,
          lastName: learnerData.lastName,
          gradeLevel: learnerData.gradeLevel,
          dateOfBirth: learnerData.dateOfBirth
        });
      }

      setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
    } catch (err) {
      // createLearner() already computes a proper message into FamilyContext's
      // error state before re-throwing (the thrown value itself may just be a
      // plain string - see FamilyContext's extractErrorMessage), so prefer that.
      setError(familyError || (err instanceof Error ? err.message : 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleComplete = () => {
    // /placement/:learnerId doesn't exist yet - it's Phase 4 scope (the
    // core learning loop). Phase 3's deliverable is reaching the dashboard
    // shell, so that's the destination until placement ships.
    navigate('/dashboard');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Welcome to Level Up Learning
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              We'll guide you through a quick setup so you can start getting evidence-based
              insights into your child's learning.
            </Typography>
            <Alert severity="info">
              Your account is ready. Let's add your first child.
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Tell us about your child
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="First Name"
                value={learnerData.firstName}
                onChange={e => handleLearnerChange('firstName', e.target.value)}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={learnerData.lastName}
                onChange={e => handleLearnerChange('lastName', e.target.value)}
                fullWidth
              />
              <TextField
                select
                label="Grade Level"
                value={learnerData.gradeLevel}
                onChange={e => handleLearnerChange('gradeLevel', e.target.value)}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="3">Grade 3</option>
                <option value="4">Grade 4</option>
                <option value="5">Grade 5</option>
              </TextField>
              <TextField
                label="Date of Birth (optional)"
                type="date"
                value={learnerData.dateOfBirth}
                onChange={e => handleLearnerChange('dateOfBirth', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              You're all set!
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {learnerData.firstName} has been added to your account. Head to your dashboard to get started.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Soon you'll be able to run a short placement check so we can show you exactly what's solid for {learnerData.firstName},
              what's still developing, and what to work on next.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ minHeight: '200px', mb: 4 }}>
          {renderStepContent()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            Back
          </Button>

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleComplete}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Go to Dashboard'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Next'}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default OnboardingWizard;
